import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { useAppSelector } from "@hooks";

interface ExportDashboardCsvDialogProps {
  open: boolean;
  onClose: () => void;
}

const ExportDashboardCsvDialog = ({
  open,
  onClose,
}: ExportDashboardCsvDialogProps) => {
  const [rangeOption, setRangeOption] = useState<"30d" | "12m" | "custom">(
    "30d",
  );
  const [customStartDate, setCustomStartDate] = useState<Dayjs | null>(
    dayjs().subtract(30, "day"),
  );
  const [customEndDate, setCustomEndDate] = useState<Dayjs | null>(dayjs());
  const [isExporting, setIsExporting] = useState(false);
  const token = useAppSelector((state) => state.auth?.token);
  const idToken = useAppSelector((state) => state.auth?.idToken);

  const formatDate = (d: Dayjs) => d.format("YYYY-MM-DD");
  const [disableExport, setDisableExport] = useState(false);

  // We deliberately use a direct authorized fetch here instead of the generated RTK Query hook because
  // this endpoint streams CSV (non-JSON). The generated hook attempts to parse JSON and reports a
  // PARSING_ERROR (even though the HTTP status is 200), which also triggers the global error alert.
  const handleExport = async () => {
    let start: string;
    let end: string;
    const today = dayjs();
    if (rangeOption === "30d") {
      start = formatDate(today.subtract(30, "day"));
      end = formatDate(today);
    } else if (rangeOption === "12m") {
      start = formatDate(today.subtract(12, "month"));
      end = formatDate(today);
    } else {
      if (!customStartDate || !customEndDate) {
        setDisableExport(true);
        return;
      }
      const s = customStartDate.isBefore(customEndDate)
        ? customStartDate
        : customEndDate;
      const e = customEndDate.isAfter(customStartDate)
        ? customEndDate
        : customStartDate;
      start = formatDate(s);
      end = formatDate(e);
    }

    try {
      setIsExporting(true);
      const baseUrl = (process.env.VITE_ORDER_SERVER_URL || "").trim();
      const path = `/ord/api/v1/dashboard/export?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
      const url = baseUrl ? new URL(path, baseUrl).toString() : path;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          authorization: token ? `Bearer ${token}` : "",
          "id-token": idToken || "",
          "requesting-app": "web",
          accept: "text/csv, text/plain, */*",
        },
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", `dashboard_export_${start}_to_${end}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      onClose();
    } catch (_) {
      // noop; optional: surface local error if needed
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (!isExporting ? onClose() : undefined)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Export Settings</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <RadioGroup
            value={rangeOption}
            onChange={(_e, value) =>
              setRangeOption(value as "30d" | "12m" | "custom")
            }
          >
            <FormControlLabel
              value="30d"
              control={<Radio />}
              label="Last 30 days"
            />
            <FormControlLabel
              value="12m"
              control={<Radio />}
              label="Last 12 months"
            />
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="Custom"
            />
          </RadioGroup>

          {rangeOption === "custom" && (
            <Stack direction="row" spacing={2} alignItems="center">
              <DatePicker
                label="Start date"
                value={customStartDate}
                onChange={(v) => setCustomStartDate(v)}
                format="YYYY-MM-DD"
              />
              <DatePicker
                minDate={customStartDate ?? undefined}
                onError={(error) => {
                  if (error) {
                    setDisableExport(true);
                  } else {
                    setDisableExport(false);
                  }
                }}
                label="End date"
                value={customEndDate}
                onChange={(v) => setCustomEndDate(v)}
                format="YYYY-MM-DD"
                slotProps={{
                  previousIconButton: {
                    sx: {
                      "&.Mui-disabled": {
                        color: "grey.400",
                        opacity: 0.5,
                      },
                    },
                  },
                  nextIconButton: {
                    sx: {
                      "&.Mui-disabled": {
                        color: "grey.400",
                        opacity: 0.5,
                      },
                    },
                  },
                }}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button onClick={handleExport} disabled={isExporting || disableExport}>
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDashboardCsvDialog;
